import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Phone, Mail, MapPin, Facebook, Instagram, Pencil, Check, X, Plus, Trash2, Star } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultZones = [
  'Finistère (29)',
  'Morbihan (56)',
  'Côtes-d\'Armor (22)'
];

const defaultCities = [
  'Quimper', 'Brest', 'Lorient', 'Vannes', 
  'Saint-Brieuc', 'Lannion', 'Concarneau', 'Morlaix',
  'Guingamp', 'Dinan', 'Paimpol', 'Lamballe'
];

const defaultLinks = [
  { label: 'Animation Mascotte', path: '/mascottes' },
  { label: 'Anniversaire à domicile', path: '/anniversaire' },
  { label: 'Spectacles', path: '/spectacles' },
  { label: 'Location Machines', path: '/location' },
];

export const Footer = () => {
  const { isAdmin, getAuthHeaders } = useAuth();
  const [footerData, setFooterData] = useState({
    description: 'Plus de 15 ans d\'expérience dans l\'animation pour enfants en Bretagne. Spectacles de magie, mascottes et machines à gourmandises.',
    phone: '06 35 51 25 82',
    email: 'contact@le-clown-magicien.com',
    facebook: '#',
    instagram: '#',
    zones: defaultZones,
    cities: defaultCities,
    links: defaultLinks,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [tempData, setTempData] = useState(footerData);

  useEffect(() => {
    loadFooterData();
  }, []);

  const loadFooterData = async () => {
    try {
      const response = await axios.get(`${API}/content/global/footer`);
      if (response.data && Object.keys(response.data).length > 0) {
        setFooterData({ ...footerData, ...response.data });
        setTempData({ ...footerData, ...response.data });
      }
    } catch (e) {}
  };

  const saveFooterData = async () => {
    try {
      await axios.put(
        `${API}/content/global/footer`,
        { content: tempData },
        { headers: getAuthHeaders() }
      );
      setFooterData(tempData);
      setIsEditing(false);
      toast.success('Pied de page mis à jour !');
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  return (
    <footer className="bg-foreground text-white relative overflow-x-hidden" data-testid="footer">
      {/* Admin Edit Button */}
      {isAdmin && !isEditing && (
        <button
          onClick={() => { setTempData(footerData); setIsEditing(true); }}
          className="absolute top-4 right-4 z-10 px-4 py-2 bg-primary text-white rounded-full flex items-center gap-2 hover:bg-primary/90"
        >
          <Pencil className="w-4 h-4" />
          Modifier le pied de page
        </button>
      )}

      {/* Wave Top */}
      <div className="relative">
        <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path 
            d="M0,30 C360,60 720,0 1080,30 C1260,45 1440,30 1440,30 L1440,60 L0,60 Z" 
            fill="#1E293B"
          />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo & Description */}
          <div className="space-y-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_magie-enfants/artifacts/prlprfaq_Mesa%20de%20trabajo3.png" 
              alt="Le Clown Magicien" 
              className="h-20 w-auto"
            />
            <p className="text-white/70 text-sm leading-relaxed">
              {footerData.description}
            </p>
            <div className="flex gap-4">
              <a 
                href={footerData.facebook}
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors"
                data-testid="footer-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href={footerData.instagram}
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Nos Services</h4>
            <ul className="space-y-2">
              {footerData.links.map((link, index) => (
                <li key={index}>
                  <Link to={link.path} className="text-white/70 hover:text-secondary transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/pourquoi-nous-choisir" className="text-white/70 hover:text-secondary transition-colors font-semibold flex items-center gap-1.5" data-testid="footer-pourquoi-nous-choisir">
                  <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                  Pourquoi nous choisir ?
                </Link>
              </li>
              <li>
                <Link to="/arbre-de-noel-entreprise-brest" className="text-white/70 hover:text-secondary transition-colors font-semibold flex items-center gap-1.5" data-testid="footer-arbre-noel-brest">
                  <Star className="w-3.5 h-3.5 text-rose-400 fill-rose-400" />
                  Arbre de Noël CSE Brest
                </Link>
              </li>
              <li>
                <Link to="/spectacle-magie-festival-medieval-bretagne" className="text-white/70 hover:text-secondary transition-colors text-sm flex items-center gap-1.5" data-testid="footer-niche-festival-medieval">
                  <Star className="w-3 h-3 text-purple-400 fill-purple-400" />
                  Festival Médiéval Bretagne
                </Link>
              </li>
              <li>
                <Link to="/spectacle-magie-rue-bretagne" className="text-white/70 hover:text-secondary transition-colors text-sm flex items-center gap-1.5" data-testid="footer-niche-rue">
                  <Star className="w-3 h-3 text-purple-400 fill-purple-400" />
                  Spectacle de Rue Bretagne
                </Link>
              </li>
              <li>
                <Link to="/spectacle-magie-mairie-comite-fetes-bretagne" className="text-white/70 hover:text-secondary transition-colors text-sm flex items-center gap-1.5" data-testid="footer-niche-mairie">
                  <Star className="w-3 h-3 text-purple-400 fill-purple-400" />
                  Mairies & Comités des Fêtes
                </Link>
              </li>
            </ul>
          </div>

          {/* Zone d'intervention */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Zone d'intervention</h4>
            <ul className="space-y-2 mb-4">
              {footerData.zones.map((zone, index) => (
                <li key={index} className="text-white/70 text-sm flex items-center gap-2">
                  <MapPin className="w-3 h-3 text-secondary" />
                  {zone}
                </li>
              ))}
            </ul>
            <p className="text-white/50 text-xs">
              {footerData.cities.join(' • ')}
            </p>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Contact</h4>
            <ul className="space-y-3">
              <li>
                <a 
                  href={`tel:${footerData.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-3 text-white/70 hover:text-secondary transition-colors"
                  data-testid="footer-phone"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-semibold">{footerData.phone}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${footerData.email}`}
                  className="flex items-center gap-3 text-white/70 hover:text-secondary transition-colors"
                  data-testid="footer-email"
                >
                  <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-secondary" />
                  </div>
                  <span>{footerData.email}</span>
                </a>
              </li>
              <li>
                <a 
                  href="https://maps.app.goo.gl/QXP8PJbgisnNyxx1A"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-white/70 hover:text-yellow-400 transition-colors"
                  data-testid="footer-google-reviews"
                >
                  <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Star className="w-5 h-5 text-yellow-400" />
                  </div>
                  <span className="flex flex-col">
                    <span className="font-semibold text-yellow-400">4.9/5 sur Google</span>
                    <span className="text-xs text-white/50">Laissez votre avis !</span>
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-center sm:text-left">
            <p className="text-white/50 text-sm">
              © {new Date().getFullYear()} Le Clown Magicien. Tous droits réservés.
            </p>
            <p className="text-white/40 text-xs mt-1">
              SIRET : 502 207 178 00059 - Guilhem Debleds
            </p>
          </div>
          <div className="flex gap-6 text-sm">
            <Link to="/mentions-legales" className="text-white/50 hover:text-white transition-colors">
              Mentions legales
            </Link>
            <span className="text-white/30">|</span>
            <Link to="/politique-confidentialite" className="text-white/50 hover:text-white transition-colors">
              Politique de confidentialite
            </Link>
            <span className="text-white/30">|</span>
            <Link to="/cgv" className="text-white/50 hover:text-white transition-colors">
              CGV
            </Link>
            <span className="text-white/30">|</span>
            <Link to="/contact" className="text-white/50 hover:text-white transition-colors">
              Contact
            </Link>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le pied de page</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea
                value={tempData.description}
                onChange={(e) => setTempData({ ...tempData, description: e.target.value })}
                className="w-full mt-1 p-2 border rounded-lg min-h-[80px]"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Téléphone</label>
                <Input
                  value={tempData.phone}
                  onChange={(e) => setTempData({ ...tempData, phone: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <Input
                  value={tempData.email}
                  onChange={(e) => setTempData({ ...tempData, email: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Lien Facebook</label>
                <Input
                  value={tempData.facebook}
                  onChange={(e) => setTempData({ ...tempData, facebook: e.target.value })}
                  className="mt-1"
                  placeholder="https://facebook.com/..."
                />
              </div>
              <div>
                <label className="text-sm font-medium">Lien Instagram</label>
                <Input
                  value={tempData.instagram}
                  onChange={(e) => setTempData({ ...tempData, instagram: e.target.value })}
                  className="mt-1"
                  placeholder="https://instagram.com/..."
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Zones d'intervention (une par ligne)</label>
              <textarea
                value={tempData.zones.join('\n')}
                onChange={(e) => setTempData({ ...tempData, zones: e.target.value.split('\n').filter(z => z.trim()) })}
                className="w-full mt-1 p-2 border rounded-lg min-h-[100px]"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Villes (séparées par des virgules)</label>
              <textarea
                value={tempData.cities.join(', ')}
                onChange={(e) => setTempData({ ...tempData, cities: e.target.value.split(',').map(c => c.trim()).filter(c => c) })}
                className="w-full mt-1 p-2 border rounded-lg"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={saveFooterData} className="flex-1">
                <Check className="w-4 h-4 mr-2" /> Sauvegarder
              </Button>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                <X className="w-4 h-4 mr-2" /> Annuler
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
};
